import { useState } from 'react';
import { COMPONENT_ART } from './componentArt.jsx';

/**
 * Component placement exercise.
 *
 * A motherboard with its connectors marked, and the parts to put in
 * them. Knowing what a component does is one thing; knowing where it
 * goes - and that a graphics card and a network card use different
 * lengths of the same kind of slot - is what makes it real.
 *
 * Two ways to place, because one of them has to work on a phone:
 * drag a component onto a connector, or tap the component then tap the
 * connector. Both end in the same state, and anything placed can be
 * moved again before verifying.
 *
 * Nothing is marked right or wrong until Verify is pressed. Being told
 * mid-placement would turn it into trial and error rather than
 * reasoning about where a part belongs.
 */

/* Positions are percentages of the board, so the whole thing scales
   with the container and needs no fixed pixel size. */
export const SLOTS = [
  { id: 'io', label: 'Rear I/O panel', accepts: 'io', x: 2.5, y: 8, w: 9, h: 44,
    why: 'The rear panel is where the keyboard, mouse and screen actually plug in.' },
  { id: 'cpu', label: 'CPU socket', accepts: 'cpu', x: 30, y: 26, w: 20, h: 30,
    why: 'The square socket in the middle, with the lever and the notched corner.' },
  { id: 'ram', label: 'DIMM slots', accepts: 'ram', x: 56, y: 8, w: 16, h: 48,
    why: 'Long thin slots with clips at each end, always right beside the processor.' },
  { id: 'psu', label: '24-pin ATX power', accepts: 'psu', x: 85, y: 10, w: 11, h: 30,
    why: 'The big keyed block on the edge of the board. Everything is powered through it.' },
  { id: 'gpu', label: 'PCIe x16 slot', accepts: 'gpu', x: 22, y: 66, w: 48, h: 10,
    why: 'The long expansion slot. A graphics card needs the widest one for the bandwidth.' },
  { id: 'network', label: 'PCIe x1 slot', accepts: 'network', x: 22, y: 82, w: 23, h: 9,
    why: 'The short expansion slot. A network card needs far less bandwidth than a GPU.' },
  { id: 'storage', label: 'M.2 / SATA', accepts: 'storage', x: 76, y: 62, w: 20, h: 14,
    why: 'The connectors drives attach to, usually along the edge or flat under a heatsink.' },
];

const PARTS = [
  { id: 'cpu', name: 'CPU' },
  { id: 'ram', name: 'RAM' },
  { id: 'gpu', name: 'Graphics Card' },
  { id: 'network', name: 'Network Adapter' },
  { id: 'storage', name: 'Storage (SSD/HDD)' },
  { id: 'psu', name: 'Power Supply (PSU)' },
  { id: 'io', name: 'Input/Output' },
];

/** The board itself. Drawn rather than photographed, like the rest. */
export function Board() {
  const BOARD = '#1d4f33';
  const DARK = '#143823';
  const TRACE = '#2f7d4f';
  const CHIP = '#2d2a45';
  const METAL = '#b9b6c9';
  const GOLD = '#e0a413';

  return (
    <svg viewBox="0 0 400 280" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="0" y="0" width="400" height="280" rx="6" fill={BOARD} />
      {/* traces */}
      {[40, 60, 80, 100, 120].map((y) => (
        <path key={y} d={`M20 ${y} H150 L170 ${y + 18} H340`} stroke={TRACE} strokeWidth="1" fill="none" opacity="0.7" />
      ))}
      {[200, 220, 240].map((y) => (
        <path key={y} d={`M30 ${y} H200 L220 ${y - 16} H370`} stroke={TRACE} strokeWidth="1" fill="none" opacity="0.7" />
      ))}
      {/* chipset heatsink */}
      <rect x="300" y="150" width="54" height="54" rx="4" fill={METAL} opacity="0.85" />
      {[158, 168, 178, 188].map((y) => (
        <rect key={y} x="306" y={y} width="42" height="4" rx="2" fill={CHIP} opacity="0.5" />
      ))}
      {/* power stages beside the socket */}
      {[0, 1, 2, 3, 4].map((i) => (
        <rect key={i} x={128} y={60 + i * 16} width="14" height="12" rx="2" fill={CHIP} />
      ))}
      {/* mounting holes */}
      {[[14, 14], [386, 14], [14, 266], [386, 266]].map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="5" fill={DARK} stroke={METAL} strokeWidth="1.5" />
      ))}
      {/* battery */}
      <circle cx="266" cy="232" r="12" fill={METAL} />
      <circle cx="266" cy="232" r="7" fill={DARK} />
      {/* fan header pins */}
      {[0, 1, 2].map((i) => (
        <rect key={i} x={186 + i * 5} y="20" width="3" height="10" fill={GOLD} />
      ))}
    </svg>
  );
}

export default function PlacementExercise({ onBack, onSolved }) {
  const [placed, setPlaced] = useState({});   // slotId -> componentId
  const [selected, setSelected] = useState(null);
  const [verified, setVerified] = useState(false);
  // Snapshots of `placed`, oldest first. There is no sequence of pages
  // to walk back through here - the moves themselves are the steps.
  const [history, setHistory] = useState([]);

  const slotFor = (componentId) =>
    Object.keys(placed).find((slotId) => placed[slotId] === componentId) ?? null;

  const place = (slotId, componentId) => {
    setHistory((h) => [...h, placed]);
    setPlaced((prev) => {
      const next = { ...prev };
      // a component lives in one slot, and a slot holds one component
      for (const [s, c] of Object.entries(next)) {
        if (c === componentId) delete next[s];
      }
      next[slotId] = componentId;
      return next;
    });
    setSelected(null);
    setVerified(false);
  };

  const clearSlot = (slotId) => {
    setHistory((h) => [...h, placed]);
    setPlaced((prev) => {
      const next = { ...prev };
      delete next[slotId];
      return next;
    });
    setVerified(false);
  };

  /**
   * Back undoes one placement before it leaves the section, so a
   * misdropped part costs one tap rather than the whole board.
   */
  const back = history.length
    ? {
        label: '← Undo last placement',
        act: () => {
          setPlaced(history[history.length - 1]);
          setHistory((h) => h.slice(0, -1));
          setSelected(null);
          setVerified(false);
        },
      }
    : { label: '← Back to Inspection', act: onBack };

  const allPlaced = Object.keys(placed).length === SLOTS.length;
  const correct = SLOTS.filter((s) => placed[s.id] === s.accepts).length;
  const allCorrect = correct === SLOTS.length;

  const verify = () => {
    setVerified(true);
    if (allCorrect) onSolved?.();
  };

  return (
    <section className="lab-section">
      <div className="place-head">
        <div>
          <h2>Component Placement Exercise</h2>
          <p className="lab-lead">
            Drag each component to its correct connector on the motherboard. On a phone, tap a
            component to select it, then tap a slot to place it.
          </p>
        </div>
        <div className="row gap-sm">
          <button type="button" className="btn btn-sm btn-ghost" onClick={back.act}>
            {back.label}
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={verify}
            disabled={!allPlaced}
            title={allPlaced ? 'Check your placements' : 'Place every component first'}
          >
            Verify
          </button>
        </div>
      </div>

      <div className="place-layout">
        {/* ---------- the parts ---------- */}
        <aside className="place-tray">
          <h3>Components</h3>
          <div className="place-parts">
            {PARTS.map((p) => {
              const Art = COMPONENT_ART[p.id];
              const at = slotFor(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  draggable={!at}
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', p.id);
                    setSelected(p.id);
                  }}
                  onClick={() => setSelected(selected === p.id ? null : p.id)}
                  className={`place-part ${selected === p.id ? 'is-selected' : ''} ${at ? 'is-used' : ''}`}
                  aria-pressed={selected === p.id}
                >
                  {selected === p.id && <span className="place-badge">Selected</span>}
                  {at && <span className="place-badge is-used">Placed</span>}
                  <span className="place-part-art">{Art ? <Art /> : null}</span>
                  <strong>{p.name}</strong>
                </button>
              );
            })}
          </div>
        </aside>

        {/* ---------- the board ---------- */}
        <div className="place-board">
          <Board />
          {SLOTS.map((s) => {
            const holds = placed[s.id];
            const part = PARTS.find((p) => p.id === holds);
            const Art = holds ? COMPONENT_ART[holds] : null;
            const state = !verified || !holds ? '' : holds === s.accepts ? 'is-right' : 'is-wrong';
            return (
              <button
                key={s.id}
                type="button"
                className={`place-slot ${holds ? 'is-filled' : ''} ${state} ${selected && !holds ? 'is-target' : ''}`}
                style={{ left: `${s.x}%`, top: `${s.y}%`, width: `${s.w}%`, height: `${s.h}%` }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const id = e.dataTransfer.getData('text/plain');
                  if (id) place(s.id, id);
                }}
                onClick={() => {
                  if (selected) place(s.id, selected);
                  else if (holds) clearSlot(s.id);
                }}
                aria-label={holds ? `${s.label}, holding ${part?.name}` : `${s.label}, empty`}
              >
                {holds ? (
                  <span className="place-slot-filled">
                    <span className="place-slot-art">{Art ? <Art /> : null}</span>
                    <span className="place-slot-name">{part?.name}</span>
                    {verified && <span className="place-mark">{holds === s.accepts ? '✓' : '✗'}</span>}
                  </span>
                ) : (
                  <span className="place-slot-hint">Tap to place</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ---------- feedback ---------- */}
      {verified && (
        <div className={`lab-done ${allCorrect ? '' : 'is-wrong'}`}>
          {allCorrect ? (
            <>
              <strong>Every component in the right connector.</strong> You could open a case now and
              know what you are looking at.
            </>
          ) : (
            <div>
              <strong>{correct} of {SLOTS.length} correct.</strong> The ones marked ✗ are in the
              wrong connector — tap a placed part to take it out and try again.
              <ul className="place-hints">
                {SLOTS.filter((s) => placed[s.id] && placed[s.id] !== s.accepts).map((s) => (
                  <li key={s.id}>
                    <strong>{s.label}:</strong> {s.why}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="lab-note">
        <strong>Tip</strong>
        <p>
          Drag each component to the connector it belongs to — or tap a component to select it, then
          tap a slot to place it. You can move a component to a different slot at any time, and
          tapping a placed component takes it back out.
        </p>
      </div>
    </section>
  );
}
